import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import ThreeBackground from '../aether/components/ThreeBackground';
import Logo from '../../components/Logo';
import StripeEscrowModal from './components/StripeEscrowModal';

export default function ContractsMarketplace() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('bounties');
  const [selectedContract, setSelectedContract] = useState(null);
  
  // Convex Backend
  const dbContracts = useQuery(api.contracts.getContracts);
  const fundContractMutation = useMutation(api.contracts.fundContract);
  const seedContractsMutation = useMutation(api.contracts.seedContracts);

  // Seed on load
  useEffect(() => {
    seedContractsMutation().catch(console.error);
  }, [seedContractsMutation]);

  const bounties = dbContracts || [];

  const handleFundSuccess = async (contractId) => {
    await fundContractMutation({ contractId });
    setSelectedContract(null); // Close modal
  };

  return (
    <div className="min-h-screen text-aether-light font-sans relative overflow-x-hidden bg-background">
      <ThreeBackground />

      {/* Top Navbar */}
      <header className="fixed top-0 w-full z-50 bg-background/40 backdrop-blur-[40px] border-b border-primary/10 h-20">
        <div className="flex justify-between items-center w-full px-8 max-w-7xl mx-auto h-full">
          <div className="cursor-pointer" onClick={() => navigate('/')}>
            <Logo />
          </div>
          <nav className="hidden md:flex items-center space-x-12">
            <span className="font-label-caps text-label-caps text-on-surface-variant hover:text-primary transition-colors duration-300 cursor-pointer" onClick={() => navigate('/curation')}>Curation</span>
            <span className="font-label-caps text-label-caps text-on-surface-variant hover:text-primary transition-colors duration-300 cursor-pointer" onClick={() => navigate('/')}>Feed</span>
            <span className="font-label-caps text-label-caps text-on-surface-variant hover:text-primary transition-colors duration-300 cursor-pointer" onClick={() => navigate('/salon')}>Salon</span>
            <span className="font-label-caps text-label-caps text-primary border-b border-primary pb-1 cursor-pointer">Contracts</span>
          </nav>
          <div className="flex items-center gap-4">
            <span className="material-symbols-outlined cursor-pointer hover:opacity-80 transition-opacity">notifications</span>
            <span className="material-symbols-outlined cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate('/curation')}>account_circle</span>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="max-w-7xl mx-auto pt-32 px-4 pb-12 flex gap-8 relative z-10">
        
        {/* Left Sidebar */}
        <div className="hidden lg:block w-64 flex-shrink-0 space-y-8">
          <div className="glass-panel p-6 rounded-2xl border border-[rgba(255,255,255,0.05)]">
            <ul className="space-y-4 text-sm font-light">
              <li className="flex items-center gap-3 cursor-pointer hover:text-primary transition-colors" onClick={() => navigate('/')}>
                <span className="material-symbols-outlined text-lg">home</span> Feed
              </li>
              <li className="flex items-center gap-3 cursor-pointer hover:text-primary transition-colors" onClick={() => navigate('/curation')}>
                <span className="material-symbols-outlined text-lg">explore</span> Curation
              </li>
              <li className="flex items-center gap-3 cursor-pointer hover:text-primary transition-colors" onClick={() => navigate('/salon')}>
                <span className="material-symbols-outlined text-lg">groups</span> Salon
              </li>
              <li className="flex items-center gap-3 cursor-pointer hover:text-primary transition-colors mt-2 pt-2 border-t border-[rgba(255,255,255,0.05)] text-on-surface-variant" onClick={() => navigate('/gaming')}>
                <span className="material-symbols-outlined text-lg">sports_esports</span> Game News
              </li>
              <li className="flex items-center gap-3 cursor-pointer hover:text-primary transition-colors mt-2 pt-2 border-t border-[rgba(255,255,255,0.05)] text-on-surface-variant" onClick={() => navigate('/architecture')}>
                <span className="material-symbols-outlined text-lg">domain</span> Architecture
              </li>
              <li className="flex items-center gap-3 cursor-pointer text-[#d4af37] mt-2 pt-2 border-t border-[rgba(255,255,255,0.05)]">
                <span className="material-symbols-outlined text-lg">work</span> Contracts
              </li>
            </ul>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 space-y-8">
          <div className="flex justify-between items-end mb-10 border-b border-white/5 pb-6">
            <div>
              <h2 className="font-playfair text-4xl text-[#d4af37] mb-2">The Nexus Marketplace</h2>
              <p className="text-sm text-on-surface-variant/60 font-light tracking-widest uppercase">Elite Bounties & Freelance Contracts</p>
            </div>
            <button className="px-6 py-2 bg-[#d4af37]/10 text-[#d4af37] border border-[#d4af37]/30 rounded-full font-label-caps uppercase tracking-widest text-xs hover:bg-[#d4af37]/20 transition-colors">
              Post a Bounty
            </button>
          </div>

          <div className="flex gap-4 mb-8">
            <button 
              className={`px-4 py-2 rounded-full font-label-caps text-xs tracking-widest uppercase transition-colors ${activeTab === 'bounties' ? 'bg-[#d4af37] text-black font-bold' : 'bg-white/5 text-white/50 border border-white/10 hover:text-white'}`}
              onClick={() => setActiveTab('bounties')}
            >
              Open Bounties
            </button>
            <button 
              className={`px-4 py-2 rounded-full font-label-caps text-xs tracking-widest uppercase transition-colors ${activeTab === 'talents' ? 'bg-[#d4af37] text-black font-bold' : 'bg-white/5 text-white/50 border border-white/10 hover:text-white'}`}
              onClick={() => setActiveTab('talents')}
            >
              Talent Roster
            </button>
          </div>

          {activeTab === 'bounties' && (
            <div className="space-y-4">
              {bounties.map((job, idx) => (
                <motion.article 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1, duration: 0.5 }}
                  key={job.id} 
                  className="glass-panel p-6 rounded-2xl border border-[rgba(255,255,255,0.05)] hover:border-[#d4af37]/30 transition-colors group cursor-pointer"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-primary mb-1 block">{job.client}</span>
                      <h3 className="font-playfair text-2xl text-white group-hover:text-[#d4af37] transition-colors">{job.title}</h3>
                    </div>
                    <div className="text-right">
                      <div className="text-[#00ffcc] font-mono text-lg">{job.budget}</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/40">{job.type}</div>
                    </div>
                  </div>
                  <p className="text-sm text-white/60 font-light mb-6 line-clamp-2">
                    {job.description}
                  </p>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        {job.tags.map(tag => (
                          <span key={tag} className="px-2 py-1 bg-white/5 border border-white/10 rounded text-[10px] text-white/70 uppercase tracking-wider">
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      {job.escrowStatus === "funded" ? (
                        <div className="px-4 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center gap-2">
                          <span className="material-symbols-outlined text-[14px] text-emerald-400">verified_user</span>
                          <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400">Escrow Funded</span>
                        </div>
                      ) : (
                        <button 
                          onClick={(e) => { e.stopPropagation(); setSelectedContract(job); }}
                          className="px-4 py-1.5 bg-indigo-500/10 border border-indigo-500/30 rounded-full text-[10px] font-bold uppercase tracking-widest text-indigo-400 flex items-center gap-1 hover:bg-indigo-500/20 transition-colors"
                        >
                          <span className="material-symbols-outlined text-[14px]">lock</span>
                          Fund Escrow
                        </button>
                      )}
                    </div>
                </motion.article>
              ))}
            </div>
          )}

          {activeTab === 'talents' && (
            <div className="text-center py-20">
              <span className="material-symbols-outlined text-4xl text-white/20 mb-4 block">engineering</span>
              <p className="text-white/40 font-light">Elite talent roster is currently being curated.</p>
            </div>
          )}

        </div>
      </div>
      
      {/* Stripe Modal */}
      <StripeEscrowModal 
        isOpen={!!selectedContract} 
        onClose={() => setSelectedContract(null)} 
        contract={selectedContract} 
        onFundSuccess={handleFundSuccess} 
      />
    </div>
  );
}
